import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  selectAvailableWorkspaces,
  selectSelectedWorkspace,
  setSelectedWorkspace,
  setWorkspaceLocked,
} from "../features/workspaceSlice";

// For modules that only ever operate on one fixed site (e.g. Optifund
// Dashboard - see docs/new-module-checklist.md Section 10.6), rather than
// the generic multi-site `workspace` param other modules read (Section
// 10.5). Call from the screen once its own permission + site-access checks
// have both passed: forces the global workspace selection to match this
// site (so the drawer's WorkspaceDropdown isn't showing a different site
// than what's actually on screen) and marks it locked so the dropdown
// disables switching for as long as this screen is mounted.
export function useLockWorkspaceToSite(siteName: string, shouldLock: boolean) {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const availableWorkspaces = useSelector(selectAvailableWorkspaces);
  const selectedWorkspace = useSelector(selectSelectedWorkspace);

  useEffect(() => {
    if (!shouldLock) return;

    const matchingWorkspace = availableWorkspaces.find(
      (workspace: any) => workspace.metadata?.site_name === siteName
    );
    if (!matchingWorkspace) return;

    if (selectedWorkspace?.id !== matchingWorkspace.id) {
      dispatch(setSelectedWorkspace(matchingWorkspace));
    }
    if (searchParams.get("workspace") !== matchingWorkspace.id) {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          next.set("workspace", matchingWorkspace.id);
          return next;
        },
        { replace: true }
      );
    }
    dispatch(setWorkspaceLocked(true));

    return () => {
      dispatch(setWorkspaceLocked(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldLock, siteName, availableWorkspaces, selectedWorkspace, dispatch]);
}
